// ============================================================================
// Core interfaces for event listeners.
// ============================================================================

/** Default permissions that all logging event listeners should support */
export type BasePermission = {
  logging: boolean;
};

// biome-ignore lint/complexity/noBannedTypes: We actually do want an empty object here, and not a Record<string, never> because we use this for typechecking <T extends EmptyObject>
export type EmptyObject = {};

/**
 * A flat context (table) that can be written out by loggers that ultimately end up in a tabular
 * format.
 */
export type FlatContext = Record<string, string | number | null | undefined>;

/**
 * A listener to events on the logging event bus.
 *
 * @param LoggingContext Interface for the context that be set on the logger via logger.setContext
 *   or via a second parameter to any logEvent/logInfo/... command.
 * @param CustomEvent Interface for any custom events that can be sent over the event bus. This
 *   framework is agnostic to web/node/cli/runtime etc, so users are encouraged to make their own
 *   events through this.
 * @param LoggingPermission Extensions to default permissions for custom listeners. The defautl
 *   permissions are meant to forwarding user permissions from marketing sites as reviewed by legal,
 *   but this is extensible for server-side applications.
 */
export type EventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvent extends EmptyObject = EmptyObject,
  LoggingPermission extends BasePermission = BasePermission
> = {
  /** Logs the event. Can be queuing it, or waiting for a batch. */
  logEvent: (event: LoggedEvent<CustomEvent>, context: Partial<LoggingContext>) => void;
  /** Turns on delivery of events that require permissions. */
  allow: (permission: keyof LoggingPermission) => Promise<void>;
  deny: (permission: keyof LoggingPermission) => Promise<void>;
  /** Sends any queued up events. Typically called before process exit. */
  flush: () => Promise<unknown>;
};

// ============================================================================
// Event interfaces
// ============================================================================

/**
 * The interface for the types of events that can be sent over the event bus. P This can be extended
 * by passing in other events through the @param OtherEvents.
 */
export type LoggedEvent<OtherEvents> =
  | InternalEvent
  | InternalValueEvent
  | InternalTimingEvent
  | UserInteractionEvent
  | WarningEvent
  | ErrorEvent
  | DebugEvent
  | CustomEvent<OtherEvents>;

/**
 * Types of events that can be sent over the event bus. All event listeners use this to
 * differentiate between events they do and do not listen to.
 *
 * This is an emalgamation of Logging Severity, Count vs Measure and User vs Internal categories.
 *
 * The default is INFO and different listeners handle custom events differently including changing
 * the logging severity for CUSTOM events.
 */
export enum LoggingEventType {
  CUSTOM = 'custom',
  DEBUG = 'debug',
  ERROR = 'error',
  INFO = 'info',
  TIMING = 'timing',
  USER_ACTION = 'user_action',
  VALUE = 'value',
  WARNING = 'warning',
}

/** Interface for any events that are not generic. */
export type CustomEvent<CustomEvents> = CustomEvents & {
  type: LoggingEventType.CUSTOM;
};

/** An error event. To be used for unrecoverable sitations that requires fixing. */
export type ErrorEvent = {
  type: LoggingEventType.ERROR;
  /** Place where the error occurred. */
  component: string;
  /** Error that was thrown. Can be anything, but we do prefer errors. */
  error?: unknown;
  /** Human readable text. Usually logged as-is */
  message?: string;
  /**
   * Specific situation that the error is occurring. Helps for specificity and if you want metrics
   * on it.
   */
  action?: string;
};

/** A warning. To be used for */
export type WarningEvent = {
  type: LoggingEventType.WARNING;
  /** Place where the error occurred. */
  component: string;
  /** Human-readable text. Usually logged as-is. */
  message?: string;
  /** Specific action that was being undertaken when the warning occurred. */
  action?: string;
};

/**
 * An event that is triggered by a user action.
 *
 * This originates on the web where it's important to track what the user does vs what performance
 * metrics get recorded.
 */
export type UserInteractionEvent = {
  type: LoggingEventType.USER_ACTION;
  /** Place where the event happened */
  component: string;
  /** What action did the user take. Suggestion is to keep this to one verb. */
  action: string;
  /** What did the action relate to. This should be specific like an ID. */
  label: string;
};

/** An info-level event not originated by the user. */
export type InternalEvent = {
  type: LoggingEventType.INFO;
  /** Place where the event happened */
  component: string;
  /** What happened. Suggest to keep this to one verb */
  action: string;
  /** What did the action relate to. Be specific. This should be an ID. */
  label?: string;
};

/**
 * An info-level log for a performance event that has a value.
 *
 * Note that this can store any value, but if you need to store duration in milliseconds use the
 * dedicated InternalTimingEvent.
 */
export type InternalValueEvent = {
  type: LoggingEventType.VALUE;
  /** Place where the measurement happened */
  component: string;
  /** Name of a metric */
  variable: string;
  /** Metric value */
  value: number;
  /** Any ID for the measured object */
  label?: string;
};

/**
 * Duration measurement event. Same as InternalValueEvent but the units are whole milliseconds.
 *
 * If you need to measure something other than duration, use InternalValueEvent.
 */
export type InternalTimingEvent = {
  type: LoggingEventType.TIMING;
  /** Place where the measurement happened */
  component: string;
  /** Name of a metric */
  variable: string;
  /** Time in milliseconds (always milliseconds) */
  valueMs: number;
  /** Any ID for the measured time */
  label?: string;
};

/** Any debug-level event. Can be thrown away or not logged */
export type DebugEvent = {
  type: LoggingEventType.DEBUG;
  /** Place where the event happened */
  component: string;
  /** Human-readable text. */
  message: string;
};

// ============================================================================
// Misc interfaces
// ============================================================================

/**
 * Tracking permissions on the web.
 *
 * These correspond directly to the tracking categories reviewed by Snap Legal as far as cookie
 * storage and what we're allowed to track for users.
 *
 * It is on implementers of EventListener interfaces to adhere to these.
 *
 * See https://snap.com/en-US/cookie-settings
 */
export type WebTrackingPermission = BasePermission & {
  marketing: boolean;
  performance: boolean;
};

/** Events collected client side to forward to hermes client event collection. */
export interface EventRequest<CustomEvent, LoggingContext> {
  events: EventPayload<CustomEvent, LoggingContext>[];
  /** Timestamp request was made. */
  timestamp: number;
}

/** Event and context payload request used for forwarding client events to hermes. */
export interface EventPayload<CustomEvent, LoggingContext> {
  event: LoggedEvent<CustomEvent>;
  context: Partial<LoggingContext>;
}
